import { useRef, useEffect, useCallback } from 'react';
import searchAdsNavigator, {
  SearchResponse,
  PageResult,
} from 'src/commons/apis/search-ads-navigator';
import { isAbortError } from 'src/commons/utils/async-request';

export const useSearch = (locale?: string) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const keywordCacheRef = useRef<Map<string, PageResult[]>>(new Map());

  const cancelPreviousRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  const makeSearchRequest = async (
    query: string,
    mode?: string,
  ): Promise<SearchResponse> => {
    const signal = cancelPreviousRequest();

    return searchAdsNavigator(
      { question: query, ...(mode && { mode }), ...(locale && { locale }) },
      signal,
    );
  };

  const keywordSearch = useCallback(
    async (query: string): Promise<PageResult[]> => {
      if (!query.trim()) return [];

      const trimmedQuery = query.trim();
      const cacheKey = locale ? `${trimmedQuery}:${locale}` : trimmedQuery;

      // Check cache first for keyword searches
      if (keywordCacheRef.current.has(cacheKey)) {
        return keywordCacheRef.current.get(cacheKey) || [];
      }

      try {
        const data = await makeSearchRequest(trimmedQuery, 'keyword');
        const pages = data.recommendedPages || [];

        keywordCacheRef.current.set(trimmedQuery, pages);

        return pages;
      } catch (error) {
        if (error instanceof Error && !isAbortError(error)) {
          console.error('Keyword search failed:', error);
        }
        return [];
      }
    },
    [locale],
  );

  const llmSearch = useCallback(
    async (query: string): Promise<PageResult[]> => {
      if (!query.trim()) return [];

      try {
        const data = await makeSearchRequest(query.trim());
        return data.recommendedPages || [];
      } catch (error) {
        if (error instanceof Error && !isAbortError(error)) {
          console.error('LLM search failed:', error);
        }
        return [];
      }
    },
    [locale],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { keywordSearch, llmSearch };
};
